import React, { useRef } from 'react'
import { Icon } from '~/components/icon'
import { LinkLabel } from './components/link-label'
import { useLinkVariants } from './hooks/use-link-props'
import * as Comp from './link.styles'
import type { TLinkProps } from './types'

const LinkRoot = ({
  size = 'md',
  children,
  icon = true,
  disabled,
  full,
  ...props
}: TLinkProps) => {
  const { pressed, setPressed, linkFocus, setLinkFocus } = useLinkVariants()
  const refLink = useRef()
  const isDisabled = !!disabled
  const componentProperties = {
    size: size,
    icon: icon,
    hasPressed: pressed,
    hasDisabled: isDisabled,
    hasFull: full,
    hasFocused: linkFocus,
  }

  const renderInternalLabel = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return
    if (React.isValidElement(child) && child.type === LinkLabel) {
      return React.cloneElement(child as React.ReactElement<TLinkProps>, {
        size,
        disabled,
      })
    }
  })

  return (
    <Comp.Root
      {...props}
      ref={refLink.current}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onFocus={() => setLinkFocus(true)}
      onBlur={() => setLinkFocus(false)}
      {...componentProperties}
    >
      <Comp.RootView {...componentProperties}>
        {renderInternalLabel}
        {icon && (
          <Icon
            symbol={'rdicon-open'}
            size={'tiny'}
            color={
              isDisabled
                ? 'colorTextNeutralDisabled'
                : 'colorTextNeutralDefault'
            }
          />
        )}
      </Comp.RootView>
    </Comp.Root>
  )
}

LinkRoot.displayName = 'Link'

export const Link = {
  Root: LinkRoot,
  Label: LinkLabel,
}

export type { TLinkProps }
